import { useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Box, ButtonBase, Stack, Tooltip, Typography } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RemoveIcon from "@mui/icons-material/Remove";
import { tokens } from "../../theme";
import { useFormat } from "../../i18n/format";
import { change, usePlaybackFormat } from "./format";

/**
 * The playback analytics chart kit. Hand-built SVG/HTML on the CMS's own
 * validated viz tokens (`tokens.vizAttention` is the one series hue; the
 * previous period is a neutral dashed reference, never a second identity
 * colour), so it follows light/dark mode with the rest of the app.
 *
 * Rules kept (see the dataviz method): one axis per chart, thin marks, a
 * crosshair + tooltip on lines, a tooltip on every bar and cell, numbers in
 * text ink never in the series colour, and status shown with an icon + words,
 * never colour alone.
 */

/* ------------------------------------------------------------------------ */
/* Trend line with crosshair                                                 */
/* ------------------------------------------------------------------------ */

export interface TrendPoint {
  label: string;
  value: number | null;
  previous?: number | null;
}

const EMPTY = "–";

const W = 800;
const H = 220;
const PAD_L = 56;
const PAD_R = 12;
const PAD_T = 12;
const PAD_B = 26;

/** A clean top tick: the smallest 1/2/5×10ⁿ at or above the peak. */
export function niceCeiling(max: number): number {
  if (!(max > 0)) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(max));
  for (const step of [1, 2, 2.5, 5, 10]) {
    if (max <= step * magnitude) return step * magnitude;
  }
  return 10 * magnitude;
}

export function TrendLine({
  points,
  format,
  showPrevious,
  ariaLabel,
  testId,
}: {
  points: TrendPoint[];
  format: (v: number) => string;
  showPrevious: boolean;
  ariaLabel: string;
  testId?: string;
}) {
  const { t } = useTranslation("analytics");
  const [hover, setHover] = useState<number | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const n = points.length;
  const values = points.flatMap((p) => [
    p.value ?? 0,
    showPrevious ? (p.previous ?? 0) : 0,
  ]);
  const max = niceCeiling(Math.max(0, ...values));
  const plotW = W - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;
  const x = (i: number) => PAD_L + (n <= 1 ? plotW / 2 : (i * plotW) / (n - 1));
  const y = (v: number) => PAD_T + plotH - (v / max) * plotH;

  const path = (key: "value" | "previous") => {
    let d = "";
    let pen = false;
    points.forEach((p, i) => {
      const v = p[key];
      if (v === null || v === undefined) {
        pen = false;
        return;
      }
      d += `${pen ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)} `;
      pen = true;
    });
    return d.trim();
  };

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);
  const labelEvery = Math.max(1, Math.ceil(n / 8));

  const onMove = (e: React.MouseEvent) => {
    const rect = boxRef.current?.getBoundingClientRect();
    if (!rect || n === 0) return;
    const fx = ((e.clientX - rect.left) / rect.width) * W;
    const i = Math.round(((fx - PAD_L) / plotW) * (n - 1));
    setHover(Math.min(n - 1, Math.max(0, i)));
  };

  const hp = hover !== null ? points[hover] : null;
  const leftPct = hover !== null ? (x(hover) / W) * 100 : 0;

  return (
    <Box
      ref={boxRef}
      sx={{ position: "relative", width: "100%" }}
      onMouseMove={onMove}
      onMouseLeave={() => setHover(null)}
      data-testid={testId}
    >
      <Box
        component="svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={ariaLabel}
        sx={{ width: "100%", height: "auto", display: "block" }}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={PAD_L}
              x2={W - PAD_R}
              y1={y(t)}
              y2={y(t)}
              stroke={tokens.divider}
              strokeWidth={1}
            />
            <text
              x={PAD_L - 8}
              y={y(t) + 4}
              fontSize={11}
              fill={tokens.inkFaint}
              textAnchor="end"
            >
              {format(t)}
            </text>
          </g>
        ))}
        {points.map((p, i) =>
          i % labelEvery === 0 || i === n - 1 ? (
            <text
              key={p.label + i}
              x={x(i)}
              y={H - 6}
              fontSize={11}
              fill={tokens.inkFaint}
              textAnchor={i === 0 ? "start" : i === n - 1 ? "end" : "middle"}
            >
              {p.label}
            </text>
          ) : null
        )}
        {showPrevious && (
          <path
            d={path("previous")}
            fill="none"
            stroke={tokens.inkFaint}
            strokeWidth={2}
            strokeDasharray="5 4"
            strokeLinejoin="round"
          />
        )}
        {points.map((p, i) => {
          // A value with no neighbour draws no line segment, so it gets a dot:
          // otherwise a single hour of data would be an empty chart.
          const isolated =
            p.value !== null &&
            (points[i - 1]?.value ?? null) === null &&
            (points[i + 1]?.value ?? null) === null;
          return isolated ? (
            <circle
              key={`dot-${i}`}
              cx={x(i)}
              cy={y(p.value as number)}
              r={3.5}
              fill={tokens.vizAttention}
            />
          ) : null;
        })}
        <path
          d={`${path("value")}`}
          fill="none"
          stroke={tokens.vizAttention}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {hover !== null && hp && (
          <g>
            <line
              x1={x(hover)}
              x2={x(hover)}
              y1={PAD_T}
              y2={PAD_T + plotH}
              stroke={tokens.inkFaint}
              strokeWidth={1}
            />
            {showPrevious &&
              hp.previous !== null &&
              hp.previous !== undefined && (
                <circle
                  cx={x(hover)}
                  cy={y(hp.previous)}
                  r={4}
                  fill={tokens.inkFaint}
                  stroke={tokens.surface}
                  strokeWidth={2}
                />
              )}
            {hp.value !== null && (
              <circle
                cx={x(hover)}
                cy={y(hp.value)}
                r={5}
                fill={tokens.vizAttention}
                stroke={tokens.surface}
                strokeWidth={2}
              />
            )}
          </g>
        )}
      </Box>
      {hp && (
        <Box
          role="tooltip"
          sx={{
            position: "absolute",
            top: 0,
            left: `${leftPct}%`,
            transform:
              leftPct > 70
                ? "translateX(calc(-100% - 12px))"
                : "translateX(12px)",
            pointerEvents: "none",
            bgcolor: tokens.surface,
            border: `1px solid ${tokens.line}`,
            borderRadius: 1,
            boxShadow: tokens.shadowCard,
            px: 1.25,
            py: 0.75,
            minWidth: 140,
            zIndex: 2,
          }}
        >
          <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
            {hp.label}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 10, height: 2, bgcolor: tokens.vizAttention }} />
            <Typography
              variant="bodySm"
              sx={{ fontWeight: "fontWeightSemibold", color: tokens.ink }}
            >
              {hp.value === null ? EMPTY : format(hp.value)}
            </Typography>
          </Stack>
          {showPrevious && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 10,
                  height: 0,
                  borderTop: `2px dashed ${tokens.inkFaint}`,
                }}
              />
              <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                {t("playbackCharts.previousValue", {
                  value:
                    hp.previous === null || hp.previous === undefined
                      ? EMPTY
                      : format(hp.previous),
                })}
              </Typography>
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
}

/** The legend for a trend with a comparison: always shown for two series. */
export function TrendLegend({
  current,
  previous,
}: {
  current: string;
  previous?: string;
}) {
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box sx={{ width: 16, height: 2, bgcolor: tokens.vizAttention }} />
        <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
          {current}
        </Typography>
      </Stack>
      {previous && (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 16, borderTop: `2px dashed ${tokens.inkFaint}` }} />
          <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
            {previous}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}

/* ------------------------------------------------------------------------ */
/* Change badge and KPI tile                                                 */
/* ------------------------------------------------------------------------ */

/**
 * Which direction is good. `up`: more is better (plays). `down`: less is
 * better (startup time, failures). `none`: neither (bitrate on a capped
 * network says little on its own).
 */
export type Better = "up" | "down" | "none";

export function ChangeBadge({
  now,
  before,
  better,
}: {
  now: number | null;
  before: number | null;
  better: Better;
}) {
  const { t } = useTranslation("analytics");
  const pf = usePlaybackFormat();
  const c = change(now, before);
  const flat = c === null || Math.abs(c) < 0.005;
  const good =
    c === null || better === "none" || flat
      ? null
      : c > 0 === (better === "up");
  const colour =
    good === null ? tokens.inkMuted : good ? tokens.success : tokens.error;
  const Icon =
    c === null || flat
      ? RemoveIcon
      : c > 0
        ? ArrowUpwardIcon
        : ArrowDownwardIcon;
  const words = t(
    c === null
      ? "playbackCharts.change.noEarlierData"
      : flat
        ? "playbackCharts.change.unchanged"
        : good === null
          ? "playbackCharts.change.changed"
          : good
            ? "playbackCharts.change.better"
            : "playbackCharts.change.worse"
  );
  return (
    <Stack
      direction="row"
      spacing={0.25}
      alignItems="center"
      sx={{ color: colour }}
      aria-label={t("playbackCharts.change.aria", {
        change: pf.change(c),
        words,
      })}
    >
      <Icon sx={{ fontSize: 14 }} />
      <Typography
        variant="meta"
        sx={{ fontWeight: "fontWeightSemibold", color: colour }}
      >
        {pf.change(c)}
      </Typography>
    </Stack>
  );
}

export function KpiTile({
  label,
  value,
  now,
  before,
  better,
  hint,
  status,
  selected,
  onClick,
  testId,
}: {
  label: string;
  value: string;
  now: number | null;
  /** The previous period's value; `undefined` = no comparison is shown. */
  before?: number | null;
  better: Better;
  hint?: string;
  /** A status chip (icon + words), for quality thresholds. */
  status?: { level: "good" | "warning" | "critical"; text: string };
  selected?: boolean;
  onClick?: () => void;
  testId?: string;
}) {
  const body = (
    <Box
      sx={{
        textAlign: "left",
        width: "100%",
        p: 1.75,
        borderRadius: 1,
        border: `1px solid ${selected ? tokens.brand : tokens.line}`,
        bgcolor: selected ? tokens.brandTintSoft : tokens.surface,
        height: "100%",
      }}
      data-testid={testId}
    >
      <Typography variant="meta" sx={{ color: tokens.inkMuted, mb: 0.5 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          typography: "headlineLg",
          fontWeight: "fontWeightSemibold",
          color: tokens.ink,
          lineHeight: 1.2,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
        {before !== undefined && (
          <ChangeBadge now={now} before={before} better={better} />
        )}
        {status && <StatusText {...status} />}
      </Stack>
      {hint && (
        <Typography variant="micro" sx={{ color: tokens.inkFaint, mt: 0.5 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
  return onClick ? (
    <ButtonBase
      onClick={onClick}
      sx={{ borderRadius: 1, display: "block", width: "100%", height: "100%" }}
      aria-pressed={selected}
    >
      {body}
    </ButtonBase>
  ) : (
    body
  );
}

export function StatusText({
  level,
  text,
}: {
  level: "good" | "warning" | "critical";
  text: string;
}) {
  const colour =
    level === "good"
      ? tokens.success
      : level === "warning"
        ? tokens.warning
        : tokens.error;
  const glyph = level === "good" ? "●" : level === "warning" ? "▲" : "■";
  return (
    <Typography
      variant="micro"
      sx={{ fontWeight: "fontWeightSemibold", color: colour }}
    >
      {glyph} {text}
    </Typography>
  );
}

/* ------------------------------------------------------------------------ */
/* Ranked bars                                                               */
/* ------------------------------------------------------------------------ */

export interface BarRow {
  key: string;
  label: string;
  sublabel?: string;
  imageUrl?: string | null;
  value: number;
  valueLabel: string;
  previous?: number;
}

export function BarList({
  rows,
  onSelect,
  better = "up",
  empty,
  testId,
}: {
  rows: BarRow[];
  onSelect?: (key: string) => void;
  better?: Better;
  empty: ReactNode;
  testId?: string;
}) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" data-testid={testId}>
        {empty}
      </Typography>
    );
  }
  return (
    <Stack spacing={0.5} data-testid={testId}>
      {rows.map((r, i) => (
        <ButtonBase
          key={r.key}
          onClick={onSelect ? () => onSelect(r.key) : undefined}
          disabled={!onSelect}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: 1,
            py: 0.75,
            borderRadius: 1,
            textAlign: "left",
            justifyContent: "flex-start",
            "&:hover": { bgcolor: tokens.rowAlt },
          }}
          data-testid={testId ? `${testId}-row-${r.key}` : undefined}
        >
          <Typography
            variant="meta"
            sx={{
              width: 18,
              color: tokens.inkFaint,
              textAlign: "right",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {i + 1}
          </Typography>
          {r.imageUrl !== undefined && (
            <Box
              sx={{
                width: 28,
                height: 40,
                borderRadius: 0.5,
                bgcolor: tokens.rowAlt,
                flexShrink: 0,
                backgroundImage: r.imageUrl ? `url("${r.imageUrl}")` : "none",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <Typography
                variant="bodySm"
                noWrap
                sx={{ fontWeight: "fontWeightMedium", color: tokens.ink }}
                title={r.label}
              >
                {r.label}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ flexShrink: 0 }}
              >
                <Typography
                  variant="bodySm"
                  sx={{
                    fontWeight: "fontWeightSemibold",
                    color: tokens.ink,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {r.valueLabel}
                </Typography>
                {r.previous !== undefined && (
                  <ChangeBadge
                    now={r.value}
                    before={r.previous}
                    better={better}
                  />
                )}
              </Stack>
            </Stack>
            {r.sublabel && (
              <Typography
                variant="micro"
                noWrap
                sx={{ color: tokens.inkFaint }}
              >
                {r.sublabel}
              </Typography>
            )}
            <Tooltip title={`${r.label}: ${r.valueLabel}`} placement="top">
              <Box
                sx={{
                  mt: 0.5,
                  height: 6,
                  width: `${Math.max(1.5, (r.value / max) * 100)}%`,
                  bgcolor: tokens.vizAttention,
                  borderRadius: "0 4px 4px 0",
                }}
              />
            </Tooltip>
          </Box>
        </ButtonBase>
      ))}
    </Stack>
  );
}

/* ------------------------------------------------------------------------ */
/* Weekday × hour heatmap                                                    */
/* ------------------------------------------------------------------------ */

/**
 * When playback starts: ISO weekday × hour, one hue light → dark (a single
 * sequential ramp mixed from the series hue into the surface), with the
 * exact count on hover.
 */
export function Heatmap({
  cells,
  testId,
}: {
  cells: { weekday: number; hour: number; plays: number }[];
  testId?: string;
}) {
  const { t } = useTranslation("analytics");
  const f = useFormat();
  const pf = usePlaybackFormat();
  // ISO weekday names from a known Monday, in the CMS language.
  const weekdays = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) =>
        f.date(Date.UTC(2024, 0, 1 + i), { weekday: "short", timeZone: "UTC" })
      ),
    [f]
  );
  const grid = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of cells) m.set(`${c.weekday}:${c.hour}`, c.plays);
    return m;
  }, [cells]);
  const hh = (h: number) => String(h).padStart(2, "0");
  const plays = (v: number) =>
    t("playbackCharts.playsCount", { count: v, formatted: pf.count(v) });
  const max = Math.max(1, ...cells.map((c) => c.plays));
  const shade = (v: number) =>
    v === 0
      ? tokens.rowAlt
      : `color-mix(in srgb, ${tokens.vizAttention} ${Math.round(
          12 + (v / max) * 88
        )}%, ${tokens.surface})`;

  return (
    <Box data-testid={testId} sx={{ overflowX: "auto" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "36px repeat(24, minmax(14px, 1fr))",
          gap: "2px",
          minWidth: 420,
        }}
      >
        <Box />
        {Array.from({ length: 24 }, (_, h) => (
          <Typography
            variant="micro"
            key={h}
            sx={{ color: tokens.inkFaint, textAlign: "center" }}
          >
            {h % 3 === 0 ? String(h).padStart(2, "0") : ""}
          </Typography>
        ))}
        {weekdays.map((wd, i) => (
          <Box key={wd} sx={{ display: "contents" }}>
            <Typography
              variant="micro"
              sx={{ color: tokens.inkMuted, lineHeight: "18px" }}
            >
              {wd}
            </Typography>
            {Array.from({ length: 24 }, (_, h) => {
              const v = grid.get(`${i + 1}:${h}`) ?? 0;
              return (
                <Tooltip
                  key={h}
                  title={t("playbackCharts.heatmap.cell", {
                    weekday: wd,
                    from: `${hh(h)}:00`,
                    to: `${hh((h + 1) % 24)}:00`,
                    plays: plays(v),
                  })}
                  placement="top"
                >
                  <Box
                    sx={{ height: 18, borderRadius: "3px", bgcolor: shade(v) }}
                    aria-label={t("playbackCharts.heatmap.cellAria", {
                      weekday: wd,
                      hour: h,
                      plays: plays(v),
                    })}
                  />
                </Tooltip>
              );
            })}
          </Box>
        ))}
      </Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
        <Typography variant="micro" sx={{ color: tokens.inkFaint }}>
          {t("playbackCharts.heatmap.fewer")}
        </Typography>
        {[0.15, 0.35, 0.55, 0.75, 1].map((f) => (
          <Box
            key={f}
            sx={{
              width: 18,
              height: 10,
              borderRadius: "2px",
              bgcolor: `color-mix(in srgb, ${tokens.vizAttention} ${Math.round(
                12 + f * 88
              )}%, ${tokens.surface})`,
            }}
          />
        ))}
        <Typography variant="micro" sx={{ color: tokens.inkFaint }}>
          {t("playbackCharts.heatmap.more", { max: pf.count(max) })}
        </Typography>
      </Stack>
    </Box>
  );
}
